import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase/client';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid,
} from 'recharts';
import './InvestorRevenue.css';

interface RevenueRow {
  distribucion_id: string;
  fecha_consulta: string;
  nombre_paciente: string;
  numero_sesion: number;
  aplica_split: boolean;
  monto_total: number;
  monto_terapeuta: number;
  monto_inversionista: number;
  nombre_inversionista: string | null;
  estado_distribucion: string;
  estado_pago: string;
}

interface InvestorRevenueProps {
  investor: { id: string; nombre: string };
}

// Colores AA-safe (texto sobre fondo claro) — se concatenan como `${color}18`
// para tintar fondos, por eso deben ser hex literal, no var(). Mismos valores
// que las tablas WCAG/estado ya usadas en InvestorConsultas.tsx/InvestorCalendar.tsx.
const COLORS = {
  pagado:     '#057A55',
  pendiente:  '#C05E00',
  parcial:    '#6B21A8',
  reembolsado:'#1A6FBF',
};

const estadoDistColor: Record<string, string> = {
  calculada:  '#1A6FBF',
  liquidada:  '#057A55',
  disputada:  '#C05E00',
};

// Colores de marcas de gráfico (Bar fill / Line stroke) — idénticos a
// --apple-blue/--apple-green/--apple-orange. Recharts ya acepta var() en
// este archivo (ver tick.fill de XAxis/YAxis más abajo), así que se
// referencian directo a los tokens.
const CHART_COLORS = {
  inversionista: 'var(--apple-blue)',
  total:         'var(--apple-green)',
  sesiones:      'var(--apple-orange)',
};

const formatCLP = (n: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);

const formatCLPCompact = (amount: number): { primary: string; secondary: string } => {
  const full = formatCLP(amount);
  if (amount >= 1_000_000) {
    const m = amount / 1_000_000;
    return {
      primary: `$${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 1 }).format(m)}M`,
      secondary: full,
    };
  }
  return { primary: full, secondary: '' };
};

const monthLabel = (ym: string): string => {
  const [y, m] = ym.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString('es-CL', { month: 'short', year: '2-digit' });
};

type ChartView = 'ingresos' | 'sesiones' | 'pagos';

const InvestorRevenue: React.FC<InvestorRevenueProps> = ({ investor }) => {
  const [rows, setRows] = useState<RevenueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [chartView, setChartView] = useState<ChartView>('ingresos');
  const [activeTab, setActiveTab] = useState<'charts' | 'table'>('charts');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('get_distribucion_inversionista', {
        p_inversionista_id: investor.id,
        p_desde: desde || null,
        p_hasta: hasta || null,
      });
      if (rpcErr) {
        setError('No se pudieron cargar los ingresos.');
        console.error('Error ingresos inversor:', rpcErr.code);
      } else {
        setRows((data as RevenueRow[]) ?? []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [investor.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalMio = rows.reduce((s, r) => s + r.monto_inversionista, 0);
  const totalGeneral = rows.reduce((s, r) => s + r.monto_total, 0);

  // Monthly grouped data for bar/line charts
  const monthlyData = useMemo(() => {
    const map = new Map<string, { mes: string; inversionista: number; total: number; sesiones: number }>();
    rows.forEach(r => {
      const ym = r.fecha_consulta.slice(0, 7); // 'YYYY-MM'
      const existing = map.get(ym) ?? { mes: monthLabel(ym), inversionista: 0, total: 0, sesiones: 0 };
      existing.inversionista += r.monto_inversionista;
      existing.total += r.monto_total;
      existing.sesiones += 1;
      map.set(ym, existing);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [rows]);

  // Payment status distribution for pie chart
  const paymentData = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach(r => {
      map.set(r.estado_pago, (map.get(r.estado_pago) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [rows]);

  const chartTooltipStyle: React.CSSProperties = {
    background: 'var(--bg-primary)',
    border: '1px solid var(--separator)',
    borderRadius: 10,
    fontSize: 12,
    color: 'var(--text-primary)',
  };

  return (
    <div className="investor-revenue">
      {/* Header */}
      <div className="irv-header">
        <h2 className="irv-header__title">
          Finanzas
        </h2>
        <p className="irv-header__subtitle">
          Análisis de ingresos y distribución
        </p>
      </div>

      {/* Filtros */}
      <div className="irv-filters">
        <div>
          <label className="irv-filters__label">Desde</label>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
            className="irv-filters__date-input" />
        </div>
        <div>
          <label className="irv-filters__label">Hasta</label>
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
            className="irv-filters__date-input" />
        </div>
        <button onClick={loadData} className="irv-btn-filter">
          <i className="fas fa-search irv-icon-gap" />Filtrar
        </button>
        {(desde || hasta) && (
          <button onClick={() => { setDesde(''); setHasta(''); setTimeout(loadData, 0); }}
            className="irv-btn-clear">
            Limpiar
          </button>
        )}
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"><div className="spinner"></div><p>Cargando finanzas...</p></div>
        </div>
      )}

      {!loading && error && (
        <div className="irv-error-banner">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="irv-empty">
          <i className="fas fa-chart-bar irv-empty__icon"></i>
          Aún no hay ingresos distribuidos en este período.
          <br />
          <span className="irv-empty__hint">Los ingresos aparecen cuando el terapeuta confirma el pago de una consulta.</span>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          {/* KPI cards */}
          <div className="irv-kpi-grid">
            {[
              { label: 'Mi participación', value: formatCLPCompact(totalMio), icon: 'fa-chart-line', color: 'var(--success-text, #057A55)' },
              { label: 'Total generado', value: formatCLPCompact(totalGeneral), icon: 'fa-coins', color: 'var(--text-muted)' },
              { label: 'Consultas', value: { primary: String(rows.length), secondary: rows.length === 1 ? 'consulta' : 'consultas' }, icon: 'fa-clipboard-list', color: 'var(--apple-blue)' },
              { label: 'Pagadas', value: { primary: String(rows.filter(r => r.estado_pago === 'pagado').length), secondary: 'de ' + rows.length }, icon: 'fa-check-circle', color: 'var(--success-text, #057A55)' },
            ].map(({ label, value, icon, color }) => (
              <div key={label} className="irv-kpi-card">
                <div className="irv-kpi-card__row">
                  <span className="irv-kpi-card__label">{label}</span>
                  <i className={`fas ${icon} irv-kpi-card__icon`} style={{ color }} />
                </div>
                <div className="irv-kpi-card__value" style={{ color }}>{value.primary}</div>
                {value.secondary && <div className="irv-kpi-card__secondary">{value.secondary}</div>}
              </div>
            ))}
          </div>

          {/* Tab switcher: charts vs table */}
          <div className="irv-tabs">
            {(['charts', 'table'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`irv-tabs__btn${activeTab === tab ? ' irv-tabs__btn--active' : ''}`}>
                <i className={`fas ${tab === 'charts' ? 'fa-chart-bar' : 'fa-table'} irv-icon-gap`} />
                {tab === 'charts' ? 'Gráficos' : 'Detalle'}
              </button>
            ))}
          </div>

          {/* ── Charts panel ── */}
          {activeTab === 'charts' && (
            <div className="irv-charts-panel">
              {/* Chart view selector */}
              <div className="irv-chart-selector">
                {([
                  { key: 'ingresos', label: 'Ingresos por mes', icon: 'fa-dollar-sign' },
                  { key: 'sesiones', label: 'Sesiones por mes', icon: 'fa-calendar-check' },
                  { key: 'pagos',    label: 'Estado de pagos', icon: 'fa-pie-chart' },
                ] as { key: ChartView; label: string; icon: string }[]).map(({ key, label, icon }) => (
                  <button key={key} onClick={() => setChartView(key)}
                    className={`irv-chart-selector__btn${chartView === key ? ' irv-chart-selector__btn--active' : ''}`}>
                    <i className={`fas ${icon} irv-icon-gap-sm`} />{label}
                  </button>
                ))}
              </div>

              {/* ── Bar chart: monthly revenue ── */}
              {chartView === 'ingresos' && (
                <div className="irv-chart-card">
                  <h3 className="irv-chart-card__title">
                    Ingresos mensuales
                  </h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={monthlyData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={v => v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                        tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                        axisLine={false} tickLine={false} width={56}
                      />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(val: any, name: any) => [
                          formatCLP(Number(val)),
                          name === 'inversionista' ? 'Mi parte' : 'Total generado',
                        ]}
                      />
                      <Legend
                        formatter={(val) => val === 'inversionista' ? 'Mi parte' : 'Total generado'}
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Bar dataKey="total" fill={CHART_COLORS.total} radius={[4, 4, 0, 0]} name="total" />
                      <Bar dataKey="inversionista" fill={CHART_COLORS.inversionista} radius={[4, 4, 0, 0]} name="inversionista" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ── Line chart: sessions per month ── */}
              {chartView === 'sesiones' && (
                <div className="irv-chart-card">
                  <h3 className="irv-chart-card__title">
                    Sesiones por mes
                  </h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={monthlyData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                      <Tooltip contentStyle={chartTooltipStyle} formatter={(val: any) => [Number(val), 'Sesiones']} />
                      <Line
                        type="monotone" dataKey="sesiones" stroke={CHART_COLORS.sesiones}
                        strokeWidth={2.5} dot={{ fill: CHART_COLORS.sesiones, r: 4 }}
                        activeDot={{ r: 6 }} name="Sesiones"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ── Pie chart: payment status ── */}
              {chartView === 'pagos' && (
                <div className="irv-chart-card">
                  <h3 className="irv-chart-card__title">
                    Estado de pagos
                  </h3>
                  <div className="irv-pie-layout">
                    <ResponsiveContainer width={220} height={220} minWidth={180}>
                      <PieChart>
                        <Pie
                          data={paymentData}
                          cx="50%" cy="50%"
                          innerRadius={60} outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {paymentData.map((entry, i) => (
                            <Cell
                              key={i}
                              /* Fallback == var(--apple-purple) — Cell.fill es un atributo SVG, se deja literal */
                              fill={(COLORS as Record<string, string>)[entry.name] ?? '#AF52DE'}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={chartTooltipStyle}
                          formatter={(val: any, name: any) => [
                            `${Number(val)} consulta${Number(val) !== 1 ? 's' : ''}`,
                            String(name ?? ''),
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Legend */}
                    <div className="irv-pie-legend">
                      {paymentData.map(entry => {
                        const color = (COLORS as Record<string, string>)[entry.name] ?? '#AF52DE';
                        const pct = Math.round((entry.value / rows.length) * 100);
                        return (
                          <div key={entry.name} className="irv-pie-legend__row">
                            <div className="irv-pie-legend__swatch" style={{ background: color }} />
                            <div>
                              <span className="irv-pie-legend__name">
                                {entry.name}
                              </span>
                              <span className="irv-pie-legend__count">
                                {entry.value} ({pct}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Table panel ── */}
          {activeTab === 'table' && (
            <div className="irv-table-wrapper">
              <table className="irv-table">
                <thead>
                  <tr>
                    {['Fecha', 'Paciente', 'Sesión', 'Total', 'Mi parte', 'Est. pago', 'Est. dist.'].map(h => (
                      <th key={h}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.distribucion_id}>
                      <td className="td--nowrap">
                        {new Date(row.fecha_consulta).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="td--paciente">
                        {row.nombre_paciente}
                      </td>
                      <td className="td--sesion">
                        #{row.numero_sesion}
                      </td>
                      <td className="td--nowrap">
                        {formatCLP(row.monto_total)}
                      </td>
                      <td className="td--mi-parte td--nowrap">
                        {formatCLP(row.monto_inversionista)}
                      </td>
                      <td>
                        {/* Color/fondo data-driven (ver COLORS) — inline a propósito */}
                        <span className="irv-status-pill" style={{
                          color: (COLORS as Record<string, string>)[row.estado_pago] ?? 'var(--text-muted)',
                          background: `${(COLORS as Record<string, string>)[row.estado_pago] ?? 'var(--text-muted)'}18`,
                        }}>
                          {row.estado_pago}
                        </span>
                      </td>
                      <td>
                        <span className="irv-status-pill" style={{
                          color: estadoDistColor[row.estado_distribucion] ?? 'var(--text-muted)',
                          background: `${estadoDistColor[row.estado_distribucion] ?? 'var(--text-muted)'}18`,
                        }}>
                          {row.estado_distribucion}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvestorRevenue;
